import type { AppConfig, Artwork } from './types';

export class ConfigError extends Error {}

type Obj = Record<string, unknown>;

function obj(v: unknown, path: string): Obj {
  if (v === undefined) return {};
  if (!v || typeof v !== 'object' || Array.isArray(v)) throw new ConfigError(`${path} must be an object`);
  return v as Obj;
}

function num(o: Obj, key: string, path: string, fallback?: number): number {
  const v = o[key];
  if (v === undefined) {
    if (fallback === undefined) throw new ConfigError(`${path}.${key} is required`);
    return fallback;
  }
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new ConfigError(`${path}.${key} must be a number`);
  return v;
}

function str(o: Obj, key: string, path: string, fallback?: string): string {
  const v = o[key];
  if (v === undefined) {
    if (fallback === undefined) throw new ConfigError(`${path}.${key} is required`);
    return fallback;
  }
  if (typeof v !== 'string') throw new ConfigError(`${path}.${key} must be a string`);
  return v;
}

function bool(o: Obj, key: string, path: string, fallback: boolean): boolean {
  const v = o[key];
  if (v === undefined) return fallback;
  if (typeof v !== 'boolean') throw new ConfigError(`${path}.${key} must be a boolean`);
  return v;
}

function oneOf<T extends string>(o: Obj, key: string, path: string, allowed: readonly T[], fallback: T): T {
  const v = o[key];
  if (v === undefined) return fallback;
  if (typeof v !== 'string' || !(allowed as readonly string[]).includes(v)) {
    throw new ConfigError(`${path}.${key} must be one of ${allowed.join(', ')}`);
  }
  return v as T;
}

const BACKENDS = ['webaudio', 'element'] as const;
const POLICIES = ['continuous', 'follow'] as const;

export function parseConfig(raw: unknown): AppConfig {
  const r = obj(raw, 'config');
  const model = obj(r.model, 'model');
  if (r.model === undefined) throw new ConfigError('model is required');
  const det = obj(r.detection, 'detection');
  const aud = obj(r.audio, 'audio');
  if (!Array.isArray(r.artworks) || r.artworks.length === 0) throw new ConfigError('artworks must be a non-empty array');

  const artworks: Artwork[] = r.artworks.map((a, i) => {
    const p = `artworks[${i}]`;
    const o = obj(a, p);
    const id = num(o, 'id', p);
    return {
      id,
      class_label: str(o, 'class_label', p, `class${id}`),
      title: str(o, 'title', p),
      artist: str(o, 'artist', p, ''),
      caption: str(o, 'caption', p, ''),
      link_url: str(o, 'link_url', p, ''),
      link_icon: str(o, 'link_icon', p, ''),
      audio_asset: str(o, 'audio_asset', p),
      volume_adjustment: num(o, 'volume_adjustment', p, 1),
    };
  });

  const wasmUrl = model.wasmUrl;
  return {
    version: num(r, 'version', 'config', 1),
    model: {
      url: str(model, 'url', 'model'),
      inputSize: num(model, 'inputSize', 'model', 640),
      wasmUrl: wasmUrl == null ? null : str(model, 'wasmUrl', 'model'),
    },
    detection: {
      intervalMs: num(det, 'intervalMs', 'detection', 200),
      enter: num(det, 'enter', 'detection', 0.85),
      hold: num(det, 'hold', 'detection', 0.6),
      requiredHits: num(det, 'requiredHits', 'detection', 6),
      lostMs: num(det, 'lostMs', 'detection', 2000),
      rawConf: num(det, 'rawConf', 'detection', 0.25),
      iou: num(det, 'iou', 'detection', 0.45),
    },
    audio: {
      backend: oneOf(aud, 'backend', 'audio', BACKENDS, 'webaudio'),
      policy: oneOf(aud, 'policy', 'audio', POLICIES, 'continuous'),
      fadeMs: num(aud, 'fadeMs', 'audio', 500),
      iosUnmute: bool(aud, 'iosUnmute', 'audio', true),
    },
    artworks,
  };
}

/** URL パラメータによる上書き(会場での調整・デバッグ用)。不正な値は無視する。 */
export function applyUrlOverrides(cfg: AppConfig, params: URLSearchParams): AppConfig {
  const n = (key: string): number | undefined => {
    const v = parseFloat(params.get(key) ?? '');
    return Number.isFinite(v) ? v : undefined;
  };
  const pick = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
    const v = params.get(key);
    return v !== null && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
  };
  return {
    ...cfg,
    model: { ...cfg.model, url: params.get('model') ?? cfg.model.url },
    detection: {
      ...cfg.detection,
      enter: n('enter') ?? cfg.detection.enter,
      hold: n('hold') ?? cfg.detection.hold,
      requiredHits: n('hits') ?? cfg.detection.requiredHits,
      lostMs: n('lost') ?? cfg.detection.lostMs,
      intervalMs: n('interval') ?? cfg.detection.intervalMs,
    },
    audio: {
      ...cfg.audio,
      backend: pick('audio', BACKENDS, cfg.audio.backend),
      policy: pick('policy', POLICIES, cfg.audio.policy),
    },
  };
}

export async function loadConfig(url: string, fetchFn: typeof fetch = fetch): Promise<AppConfig> {
  const res = await fetchFn(url, { cache: 'no-cache' });
  if (!res.ok) throw new ConfigError(`config fetch failed: ${res.status}`);
  return parseConfig(await res.json());
}
